// Custom hook for dashboard data following ReactJS instructions
import { useMemo } from 'react';
import { useITopFiveApiV2EndpointsAuthMeEndpoint } from '@/api/api.generated';
import type { ITopFiveApiV2EndpointsAuthMeResponse } from '@/api/models';

interface DashboardData {
  apiUser: ITopFiveApiV2EndpointsAuthMeResponse | undefined;
  displayName: string;
  welcomeName: string;
  isLoading: boolean;
  error: string | null;
}

export const useDashboardData = (): DashboardData => {
  const {
    data: authResponse,
    isLoading,
    error,
  } = useITopFiveApiV2EndpointsAuthMeEndpoint({
    query: {
      enabled: true,
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: (failureCount, error: unknown) => {
        // Don't retry on 4xx errors
        const errorWithResponse = error as { response?: { status?: number } };
        if (
          errorWithResponse?.response?.status &&
          errorWithResponse.response.status >= 400 &&
          errorWithResponse.response.status < 500
        ) {
          return false;
        }
        return failureCount < 2;
      },
    },
  });

  const apiUser = authResponse?.data;

  // Memoize expensive computations
  const displayName = useMemo(() => {
    if (!apiUser) return 'User';

    if (apiUser.firstName && apiUser.lastName) {
      return `${apiUser.firstName} ${apiUser.lastName}`;
    }

    return (
      apiUser.preferredName || apiUser.firstName || apiUser.lastName || 'User'
    );
  }, [apiUser]);

  const welcomeName = useMemo(() => {
    if (!apiUser) return '';
    return apiUser.preferredName || apiUser.firstName || '';
  }, [apiUser]);

  const errorMessage = useMemo(() => {
    if (!error) return null;

    // Provide meaningful error messages based on error type
    const errorWithResponse = error as { response?: { status?: number } };
    if (errorWithResponse?.response?.status === 401) {
      return 'Authentication required. Please log in again.';
    }
    if (errorWithResponse?.response?.status === 403) {
      return 'You do not have permission to access this resource.';
    }
    if (
      errorWithResponse?.response?.status &&
      errorWithResponse.response.status >= 500
    ) {
      return 'Server error. Please try again later.';
    }
    return 'Failed to load user data. Please try again.';
  }, [error]);

  return {
    apiUser,
    displayName,
    welcomeName,
    isLoading,
    error: errorMessage,
  };
};
